/**
 * 위례세움교회 소개 페이지
 */

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { MainLayout } from '../../../../shared/components';
import { theme } from '../../../../shared/styles/theme';
import { mediaQueries } from '../../../../shared/constants/breakpoints';
import { CHURCH_INFO } from '../../data/churchInfo';
import church1 from '../../../../assets/images/church_1.JPG';
import church2 from '../../../../assets/images/church_2.JPG';
import church3 from '../../../../assets/images/church_3.JPG';
import church4 from '../../../../assets/images/church_4.JPG';
import church5 from '../../../../assets/images/church_5.JPG';
import church6 from '../../../../assets/images/church_6.jpg';

interface ChurchInfo {
  name: string;
  vision: string;
  address: string;
  phone: string;
  email: string;
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 800px;
  height: 400px;
  margin: 0 auto ${theme.spacing.xl};
  position: relative;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    height: 250px;
  }
`;

const ChurchImage = styled.img<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => props.isActive ? 1 : 0};
  transition: opacity 1s ease-in-out;
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing.xxxl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  border-bottom: 3px solid ${theme.colors.primary};
  padding-bottom: ${theme.spacing.md};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xl};
  }
`;

const VisionBox = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  padding: ${theme.spacing.xxl};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg};
  }
`;

const VisionText = styled.p`
  font-size: ${theme.fontSizes.xl};
  line-height: 1.8;
  margin: 0;

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const ContentText = styled.p`
  font-size: ${theme.fontSizes.lg};
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
`;

const InfoCard = styled.div`
  background-color: ${theme.colors.hover};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
`;

const InfoLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
`;

const InfoValue = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  font-weight: 700;
`;

export const AboutChurch: React.FC = () => {
  const churchInfo = CHURCH_INFO;
  const images = [church1, church2, church3, church4, church5, church6];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2000); // 2초마다 이미지 변경

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <MainLayout>
      <PageContainer>
        <PageTitle>{churchInfo.name}</PageTitle>

        <SliderContainer>
          {images.map((image, index) => (
            <ChurchImage
              key={index}
              src={image}
              alt={`위례세움교회 ${index + 1}`}
              isActive={index === currentImageIndex}
            />
          ))}
        </SliderContainer>

        <Section>
          <SectionTitle>교회 비전</SectionTitle>
          <VisionBox>
            <VisionText>{churchInfo.vision}</VisionText>
          </VisionBox>
        </Section>

        <Section>
          <SectionTitle>교회 소개</SectionTitle>
          <ContentText>
            위례세움교회는 2015년에 설립된 젊고 역동적인 교회입니다. 우리는 하나님의 말씀을
            중심으로 성도들이 함께 성장하고, 지역 사회를 섬기며, 하나님 나라를 확장해 나가는
            교회를 꿈꿉니다.
          </ContentText>
          <ContentText>
            모든 성도가 그리스도의 제자로서 삶의 현장에서 복음을 전하고, 이웃을 사랑으로
            섬기며, 하나님의 영광을 드러내는 삶을 살아가도록 돕고 있습니다.
          </ContentText>
        </Section>

        <Section>
          <SectionTitle>연락처 정보</SectionTitle>
          <InfoGrid>
            <InfoCard>
              <InfoLabel>주소</InfoLabel>
              <InfoValue>{churchInfo.address}</InfoValue>
            </InfoCard>
            <InfoCard>
              <InfoLabel>전화</InfoLabel>
              <InfoValue>{churchInfo.phone}</InfoValue>
            </InfoCard>
            <InfoCard>
              <InfoLabel>이메일</InfoLabel>
              <InfoValue>{churchInfo.email}</InfoValue>
            </InfoCard>
          </InfoGrid>
        </Section>
      </PageContainer>
    </MainLayout>
  );
};
